import React from 'react';
import PropTypes from 'prop-types';
import useIntl from '@afosto/utils-react/intl/useIntl';
import { useConfirm } from '@afosto/app-components-react/ConfirmDialog';
import { useOrderContext } from '../../OrderProvider';
import Button from '@material-ui/core/Button';
import { orders as ordersApi } from '@afosto/order/api/Orders';
import { useSnackbar } from 'notistack';
import translations from './translations';

const CalculationActions = ({ calculation }) => {
  const confirm = useConfirm();
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { fetchOrder, order } = useOrderContext();

  const authorizeCalculation = async () => {
    try {
      const confirmResponse = await confirm({
        title: intl.formatMessage(translations.confirmAuthorizeCalculation.title),
        description: intl.formatMessage(translations.confirmAuthorizeCalculation.description),
        catchOnCancel: false,
        buttons: [
          {
            label: intl.formatMessage(translations.confirmAuthorizeCalculation.buttons.cancel),
            type: 'cancel',
            variant: 'outlined',
            style: {
              marginRight: 'auto',
            },
          },
          {
            label: intl.formatMessage(translations.confirmAuthorizeCalculation.buttons.authorize),
            response: 'authorize',
            type: 'submit',
            variant: 'contained',
            color: 'primary',
          },
        ],
      });

      if (confirmResponse === 'authorize') {
        const response = await ordersApi.authorizeOrder(order?.id, {
          calculation_id: calculation?.id,
        });
        const { data = {} } = response;

        if (data?.is_accepted) {
          enqueueSnackbar(intl.formatMessage(translations.feedback.calculationAuthorized), {
            variant: 'success',
          });
        }

        if (!data?.is_accepted && !data?.transaction_secret) {
          enqueueSnackbar(intl.formatMessage(translations.feedback.calculationNoCreditAvailable), {
            variant: 'warning',
          });
        }

        if (!data?.is_accepted && data?.transaction_secret) {
          enqueueSnackbar(intl.formatMessage(translations.feedback.calculationNotPaid), {
            variant: 'warning',
          });
        }

        await fetchOrder(order?.id);
      }
    } catch (err) {
      enqueueSnackbar(intl.formatMessage(translations.feedback.calculationNotAuthorized), {
        variant: 'error',
      });
    }
  };

  const isValidCalculation = calculation?.is_valid;
  const hasPaymentMethod = !!order?.settings?.billing?.method_code;
  const orderIsAccepted = order?.state?.is_accepted ?? false;

  if (!isValidCalculation || !hasPaymentMethod || orderIsAccepted) {
    return null;
  }

  return (
    <Button size="small" variant="outlined" onClick={authorizeCalculation}>
      {intl.formatMessage(translations.actions.authorize)}
    </Button>
  );
};

CalculationActions.propTypes = {
  calculation: PropTypes.object,
};

CalculationActions.defaultProps = {
  calculation: undefined,
};

export default CalculationActions;
